import { UploadOutlined, DeleteOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { Form, Upload, Button as AntButton, Modal } from "antd";
import React from "react";
import { useMode } from "../../organisms";
import { deleteAttach, NEST_REST_URL, PATH } from "../../../service/client";
import Cookies from "js-cookie";

export const Attachments: React.FC<{ control: any }> = ({ control }) => {
  const { mode } = useMode();

  const getAuthenticatedUrl = (rawUrl: string | null, fileId?: any) => {
    let targetUrl = rawUrl || (fileId ? `${NEST_REST_URL}/attachments/${fileId}/` : null);
    if (!targetUrl) return null;
    const token = Cookies.get("accessToken");
    if (token && !targetUrl.includes("token=")) {
      const separator = targetUrl.includes("?") ? "&" : "?";
      targetUrl = `${targetUrl}${separator}token=${token}`;
    }
    return targetUrl;
  };

  const initialFileList = React.useMemo(() => {
    if (!Array.isArray(control?.value)) return [];
    return control.value.map((file: any) => ({
      uid: String(file.id ?? file.uid ?? Math.random()),
      name: file.name,
      status: "done" as const,
      url: getAuthenticatedUrl(file.url, file.id) || file.url,
      id: file.id,
    }));
  }, [control?.value]);

  const [fileList, setFileList] = React.useState<any[]>(initialFileList);

  // ⬇️ nowość: jeśli ktoś kliknie "Usuń" zanim przyjdzie id, zapamiętujemy uid
  const pendingDeleteUids = React.useRef<Set<string>>(new Set());

  const props = {
    onRemove: () => false,
    defaultFileList: initialFileList,
  };

  const handleChange = ({ fileList: next }: any) => setFileList(next);

  // ⬇️ dopuszczamy też przypadek, gdy response to "gołe ID" (number lub string)
  const getBackendId = (file: any) => {
    const resp = file?.response;
    if (typeof resp === "number") return resp;
    if (typeof resp === "string" && /^\d+$/.test(resp)) return Number(resp);
    return file?.id ?? resp?.id ?? file?.attach_id;
  };

  // Rozszerzenia, które przeglądarka potrafi otworzyć (backend podmieni wtedy
  // Content-Disposition na "inline", żeby plik otworzył się zamiast pobierać)
  const VIEWABLE_EXTENSIONS = ["pdf", "png", "jpg", "jpeg", "gif", "webp", "bmp", "svg", "ico", "txt"];

  const isViewableInBrowser = (fileName?: string) => {
    if (!fileName) return false;
    const ext = fileName.split(".").pop()?.toLowerCase();
    return !!ext && VIEWABLE_EXTENSIONS.includes(ext);
  };

  const handlePreview = (file: any) => {
    // Existing files already have url; newly uploaded ones get it from response
    const id = getBackendId(file);
    let url = getAuthenticatedUrl(file.url, id);
    if (url) {
      if (isViewableInBrowser(file.name)) {
        const separator = url.includes("?") ? "&" : "?";
        url = `${url}${separator}inline=true`;
      }
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const handleRemove = (file: any) => {
    Modal.confirm({
      title: "Usuń załącznik",
      icon: <ExclamationCircleOutlined />,
      content: "Czy na pewno chcesz usunąć wybrany załącznik?",
      okText: "Tak",
      cancelText: "Nie",
      onOk: async () => {
        try {
          const id = getBackendId(file);
          if (id) {
            await deleteAttach(id);
          } else {
            pendingDeleteUids.current.add(file.uid);
          }
          setFileList((prev) => prev.filter((f: any) => f.uid !== file.uid));
        } catch {
          // celowo bez toastów – minimalna zmiana
        }
      },
    });
  };

  const removeIcon = (file: any) => (
    <a
      className="ant-upload-list-item-action"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleRemove(file);
      }}
      aria-label="Usuń"
      title="Usuń"
    >
      <DeleteOutlined />
    </a>
  );

  const customRequest = (options: any) => {
    const { file, onSuccess, onError, onProgress } = options;

    const formData = new FormData();
    formData.append("file", file);

    const xhr = new XMLHttpRequest();
    const uploadUrl = control.url?.startsWith(PATH)
      ? NEST_REST_URL + control.url.slice(PATH.length)
      : control.url;
    xhr.open("POST", uploadUrl, true);
    xhr.withCredentials = true;
    xhr.setRequestHeader(
      "Authorization",
      `Bearer ${Cookies.get("accessToken")}`
    );

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress({ percent: (event.loaded / event.total) * 100 });
      }
    };

    xhr.onload = () => {
      // ⬇️ backend może zwrócić 200 lub 201
      if (xhr.status === 200 || xhr.status === 201) {
        try {
          let json: any = undefined;
          try {
            json = JSON.parse(xhr.responseText || "{}");
          } catch {
            json = xhr.responseText;
          }

          // jeżeli backend zwrócił gołe ID (np. 123 albo "123"), zamień na { id: 123 }
          if (typeof json === "number") json = { id: json };
          if (typeof json === "string" && /^\d+$/.test(json)) {
            json = { id: Number(json) };
          }

          const uid = (options.file && (options.file as any).uid) || undefined;

          // jeśli ktoś kliknął "Usuń" zanim przyszedł id → kasujemy teraz z użyciem id z odpowiedzi
          if (uid && pendingDeleteUids.current.has(uid)) {
            const newId =
              (json && json.id) ??
              (json && json.attachment_id) ??
              undefined;

            if (newId) {
              deleteAttach(newId).finally(() => {
                pendingDeleteUids.current.delete(uid);
              });
            } else {
              // brak id w odpowiedzi – trudno, wyczyść znacznik, żeby nie wisiał
              pendingDeleteUids.current.delete(uid);
            }
          }

          onSuccess(json, file); // ważne: przekaż { id } do Upload, żeby file.response.id było dostępne

          // Ustaw URL na pliku, żeby był klikalny od razu po uploadzie
          const newId = json?.id;
          const newUrl = getAuthenticatedUrl(json?.url, newId) || `${NEST_REST_URL}/attachments/${newId}/`;
          if (newId) {
            setFileList((prev) =>
              prev.map((f) =>
                f.uid === uid
                  ? { ...f, url: newUrl, id: newId }
                  : f
              )
            );
          }
        } catch {
          onSuccess("Upload successful", file);
        }
      } else {
        onError("Upload failed");
      }
    };

    xhr.onerror = () => {
      // upload padł – wyczyść ewentualne oczekujące usunięcie
      const uid = (options.file && (options.file as any).uid) || undefined;
      if (uid) pendingDeleteUids.current.delete(uid);
      onError("Upload failed");
    };

    xhr.send(formData);
  };

  return (
    <Form.Item
      label={control.field_name}
    // tooltip={
    //   mode === "edit" && !control.writeable
    //     ? "Brak uprawnień do edycji"
    //     : control.description
    // }
    >
      <Upload
        {...props}
        multiple
        customRequest={customRequest}
        fileList={fileList}
        onChange={handleChange}
        onPreview={handlePreview}
        showUploadList={{
          showRemoveIcon: true,
          removeIcon,
        }}
      >
        <AntButton
          disabled={mode === "edit" || !control.writeable}
          icon={<UploadOutlined />}
        >
          Dodaj plik
        </AntButton>
      </Upload>
    </Form.Item>
  );
};
